import { ShieldCheck, Gavel, HandCoins, FileWarning, BadgeDollarSign, Briefcase, Sparkles, AlertTriangle, CheckCircle2, XCircle, CircleCheck } from "lucide-react";
import { fileUrl } from "@/lib/api";

// Maps a credit score (300-850) to a 0-100% position on the range bar.
const scorePct = (s) => Math.max(0, Math.min(100, ((s - 300) / (850 - 300)) * 100));

const bandColor = (score) => {
  if (!score) return { text: "text-slate-600", bg: "bg-slate-100", label: "Verified" };
  if (score >= 750) return { text: "text-emerald-700", bg: "bg-emerald-100", label: "Excellent" };
  if (score >= 700) return { text: "text-emerald-700", bg: "bg-emerald-100", label: "Good" };
  if (score >= 640) return { text: "text-amber-700", bg: "bg-amber-100", label: "Fair" };
  return { text: "text-red-700", bg: "bg-red-100", label: "Poor" };
};

const recStyle = (rec) => {
  if (rec === "Accept") return { bg: "bg-emerald-50 border-emerald-200 text-emerald-800", icon: CheckCircle2 };
  if (rec === "Decline") return { bg: "bg-red-50 border-red-200 text-red-800", icon: XCircle };
  return { bg: "bg-amber-50 border-amber-200 text-amber-800", icon: AlertTriangle };
};

const RecordCard = ({ icon: Icon, label, count }) => {
  const clean = !count || count === 0;
  return (
    <div
      data-testid={`screening-record-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex flex-col items-center text-center rounded-xl border border-border bg-white px-3 py-4 gap-2"
    >
      <span className={`grid place-items-center w-9 h-9 rounded-full ${clean ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className={`font-display text-2xl font-bold leading-none ${clean ? "text-slate-800" : "text-red-600"}`}>{count ?? 0}</span>
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</span>
    </div>
  );
};

export function ScreeningReport({ profile, compact = false }) {
  const s = profile?.screening || {};
  const score = s.credit_score || profile?.credit_score || 0;
  const band = bandColor(score);
  const monthly = s.monthly_income || (profile?.annual_income ? Math.round(profile.annual_income / 12) : null);
  const ai = s.ai_snapshot;
  const rec = s.recommendation;
  const Rec = rec ? recStyle(rec) : null;

  return (
    <div
      data-testid="screening-report"
      className={`bg-white border border-border rounded-2xl overflow-hidden ${compact ? "" : "shadow-sm"}`}
    >
      <div className="flex items-center gap-2 px-6 pt-5 pb-3 border-b border-border">
        {profile?.photo_url && (
          <img src={fileUrl(profile.photo_url)} alt="profile" className="w-8 h-8 rounded-full object-cover border border-border" />
        )}
        <ShieldCheck className="w-4 h-4 text-primary" />
        <span className="font-display font-semibold tracking-tight">Screening Report</span>
        <span className="ml-auto text-[11px] uppercase tracking-wide text-muted-foreground">{s.provider || "TransUnion SmartMove"} · Mocked</span>
      </div>

      {/* Recommendation banner */}
      {rec && (
        <div data-testid="screening-recommendation" className={`mx-6 mt-4 flex items-center gap-2 rounded-xl border px-4 py-2.5 ${Rec.bg}`}>
          <Rec.icon className="w-4 h-4 shrink-0" />
          <span className="text-sm font-semibold">SmartMove recommendation: {rec}</span>
          {s.resident_score ? <span className="ml-auto text-xs font-medium">ResidentScore <b>{s.resident_score}</b></span> : null}
        </div>
      )}

      <div className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-6 p-6">
        {/* Credit score */}
        <div>
          <div className="flex items-end gap-3">
            <span className="font-display text-5xl font-extrabold tracking-tighter text-slate-900 leading-none">{score || "—"}</span>
            <span className={`mb-1 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${band.bg} ${band.text}`}>
              {band.label} score
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Credit score · range 300–850</div>
          {/* Range bar */}
          <div className="relative mt-4 h-2.5 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-500">
            {score > 0 && (
              <span
                className="absolute -top-1 w-4 h-4 rounded-full bg-white border-2 border-slate-800 shadow -translate-x-1/2"
                style={{ left: `${scorePct(score)}%` }}
              />
            )}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5">
            <span>Poor</span><span>Fair</span><span>Good</span><span>Excellent</span>
          </div>

          {/* Income */}
          <div className="mt-6 flex items-center gap-3 rounded-xl border border-border bg-secondary/40 p-4">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <BadgeDollarSign className="w-5 h-5" />
            </span>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">Income &amp; Employment</div>
              <div className="font-display text-lg font-bold text-slate-900">
                {monthly ? `$${monthly.toLocaleString()}` : "—"} <span className="text-sm font-medium text-muted-foreground">/ month</span>
                {s.income_to_rent_ratio ? <span className="ml-2 text-xs font-semibold text-emerald-700">{s.income_to_rent_ratio}× rent</span> : null}
              </div>
            </div>
            <span className="ml-auto flex items-center gap-1 text-xs text-emerald-700 font-medium">
              <Briefcase className="w-3.5 h-3.5" /> {profile?.employment || "Verified"}
            </span>
          </div>
        </div>

        {/* Record cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 md:w-[380px] gap-3 content-start">
          <RecordCard icon={ShieldCheck} label="Criminal" count={s.criminal_records} />
          <RecordCard icon={Gavel} label="Evictions" count={s.eviction_records} />
          <RecordCard icon={HandCoins} label="Collections" count={s.collection_records} />
          <RecordCard icon={FileWarning} label="Public Records" count={s.public_records} />
        </div>
      </div>

      {/* AI tenant snapshot */}
      {ai?.summary && (
        <div data-testid="ai-snapshot" className="mx-6 mb-6 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="font-display font-semibold text-sm tracking-tight">AI Tenant Snapshot</span>
          </div>
          <p className="text-sm text-slate-700">{ai.summary}</p>
          {(ai.highlights?.length > 0 || ai.risk_flags?.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ai.highlights?.map((h, i) => (
                <span key={`h${i}`} className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                  <CircleCheck className="w-3 h-3" /> {h}
                </span>
              ))}
              {ai.risk_flags?.map((r, i) => (
                <span key={`r${i}`} className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                  <AlertTriangle className="w-3 h-3" /> {r}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
